package kpt.core.database.currency.dao

import androidx.room3.EntityDeleteOrUpdateAdapter
import androidx.room3.EntityInsertAdapter
import androidx.room3.EntityUpsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.coroutines.createFlow
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.reflect.KClass
import kotlinx.coroutines.flow.Flow
import kpt.core.database.currency.entity.ExchangeRatesEntity

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "OPT_IN_USAGE_ERROR", "OPT_IN_USAGE", "MemberExtensionConflict", "CAN_BE_VAL"])
internal class ExchangeRatesDao_Impl(
  __db: RoomDatabase,
) : ExchangeRatesDao {
  private val __db: RoomDatabase

  private val __upsertAdapterOfExchangeRatesEntity: EntityUpsertAdapter<ExchangeRatesEntity>
  init {
    this.__db = __db
    this.__upsertAdapterOfExchangeRatesEntity = EntityUpsertAdapter<ExchangeRatesEntity>(object : EntityInsertAdapter<ExchangeRatesEntity>() {
      protected override fun createQuery(): String = "INSERT INTO `exchange_rates` (`baseCurrency`,`date`,`ratesJson`,`fetchedAt`) VALUES (?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: ExchangeRatesEntity) {
        statement.bindText(1, entity.baseCurrency)
        statement.bindText(2, entity.date)
        statement.bindText(3, entity.ratesJson)
        statement.bindLong(4, entity.fetchedAt)
      }
    }, object : EntityDeleteOrUpdateAdapter<ExchangeRatesEntity>() {
      protected override fun createQuery(): String = "UPDATE `exchange_rates` SET `baseCurrency` = ?,`date` = ?,`ratesJson` = ?,`fetchedAt` = ? WHERE `baseCurrency` = ?"

      protected override fun bind(statement: SQLiteStatement, entity: ExchangeRatesEntity) {
        statement.bindText(1, entity.baseCurrency)
        statement.bindText(2, entity.date)
        statement.bindText(3, entity.ratesJson)
        statement.bindLong(4, entity.fetchedAt)
        statement.bindText(5, entity.baseCurrency)
      }
    })
  }

  public override suspend fun upsert(entity: ExchangeRatesEntity): Unit = performSuspending(__db, false, true) { _connection ->
    __upsertAdapterOfExchangeRatesEntity.upsert(_connection, entity)
  }

  public override fun getByBase(currency: String): Flow<ExchangeRatesEntity?> {
    val _sql: String = "SELECT * FROM exchange_rates WHERE baseCurrency = ? LIMIT 1"
    return createFlow(__db, false, arrayOf("exchange_rates")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, currency)
        val _columnIndexOfBaseCurrency: Int = getColumnIndexOrThrow(_stmt, "baseCurrency")
        val _columnIndexOfDate: Int = getColumnIndexOrThrow(_stmt, "date")
        val _columnIndexOfRatesJson: Int = getColumnIndexOrThrow(_stmt, "ratesJson")
        val _columnIndexOfFetchedAt: Int = getColumnIndexOrThrow(_stmt, "fetchedAt")
        val _result: ExchangeRatesEntity?
        if (_stmt.step()) {
          val _tmpBaseCurrency: String
          _tmpBaseCurrency = _stmt.getText(_columnIndexOfBaseCurrency)
          val _tmpDate: String
          _tmpDate = _stmt.getText(_columnIndexOfDate)
          val _tmpRatesJson: String
          _tmpRatesJson = _stmt.getText(_columnIndexOfRatesJson)
          val _tmpFetchedAt: Long
          _tmpFetchedAt = _stmt.getLong(_columnIndexOfFetchedAt)
          _result = ExchangeRatesEntity(_tmpBaseCurrency,_tmpDate,_tmpRatesJson,_tmpFetchedAt)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteByBase(currency: String) {
    val _sql: String = "DELETE FROM exchange_rates WHERE baseCurrency = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, currency)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteAll() {
    val _sql: String = "DELETE FROM exchange_rates"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteOlderThan(epochMillis: Long) {
    val _sql: String = "DELETE FROM exchange_rates WHERE fetchedAt < ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, epochMillis)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
